import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  PrimaryButton,
  DefaultButton,
  makeStyles,
  Theme,
} from '@bb-ui/react-library';
import { useTranslation } from 'react-i18next';
import { Warning } from '@bb-ui/icons/dist/small/Warning';

interface NotificationConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  downtimeDate: string | null;
  sitesCount: number;
  loading?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  dialogPaper: {
    minWidth: '500px',
    maxWidth: '600px',
  },
  warningHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(3),
    color: theme.palette.warning.main,
  },
  warningIcon: {
    fontSize: '24px',
  },
  contentSection: {
    marginBottom: theme.spacing(3),
  },
  downtimeSection: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.grey[300]}`,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  downtimeLabel: {
    fontSize: '0.813rem',
    fontWeight: 400,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  downtimeDate: {
    fontSize: '0.938rem',
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  actionsList: {
    marginLeft: theme.spacing(1),
    marginBottom: theme.spacing(2),
    '& li': {
      marginBottom: theme.spacing(1),
      lineHeight: 1.6,
    },
  },
  restrictionBox: {
    padding: theme.spacing(2.5),
    backgroundColor: theme.palette.warning.light,
    borderRadius: theme.shape.borderRadius,
    border: `2px solid ${theme.palette.warning.main}`,
    marginTop: theme.spacing(2),
  },
  restrictionText: {
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    gap: theme.spacing(1.5),
  },
  confirmButton: {
    minWidth: '200px',
  },
}));

const NotificationConfirmDialog: React.FC<NotificationConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  downtimeDate,
  sitesCount,
  loading = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const formattedDate = downtimeDate
    ? new Date(downtimeDate).toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const handleConfirm = () => {
    if (!loading) {
      onConfirm();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={!loading ? onClose : undefined}
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: classes.dialogPaper,
      }}
    >
      <DialogTitle onClose={!loading ? onClose : undefined} closeLabel={t('global.close')}>
        {t('cohorts.notificationConfirmDialog.title')}
      </DialogTitle>

      <DialogContent>
        <div className={classes.warningHeader}>
          <Warning className={classes.warningIcon} />
          <Typography variant="h6">
            {t('cohorts.notificationConfirmDialog.warningHeader')}
          </Typography>
        </div>

        <div className={classes.contentSection}>
          <Typography
            variant="body1"
            gutterBottom
            dangerouslySetInnerHTML={{
              __html: t('cohorts.notificationConfirmDialog.description', { sitesCount }),
            }}
          />

          <div className={classes.downtimeSection}>
            <Typography variant="body2" className={classes.downtimeLabel}>
              {t('cohorts.notificationConfirmDialog.downtimeLabel')}
            </Typography>
            <Typography variant="body1" className={classes.downtimeDate}>
              {formattedDate} ({userTimezone})
            </Typography>
          </div>
        </div>

        <div className={classes.contentSection}>
          <Typography variant="body1" gutterBottom>
            {t('cohorts.notificationConfirmDialog.actions.title')}
          </Typography>
          <ul className={classes.actionsList}>
            <li>{t('cohorts.notificationConfirmDialog.actions.sendEmails')}</li>
            <li>{t('cohorts.notificationConfirmDialog.actions.updateStatus')}</li>
            <li>{t('cohorts.notificationConfirmDialog.actions.allowReminders')}</li>
          </ul>
        </div>

        <div className={classes.restrictionBox}>
          <Typography className={classes.restrictionText} color="textPrimary">
            <span>{t('cohorts.notificationConfirmDialog.restriction.warning')}</span>
            {t('cohorts.notificationConfirmDialog.restriction.text')}
          </Typography>
        </div>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <DefaultButton onClick={onClose} disabled={loading}>
          {t('cohorts.notificationConfirmDialog.buttons.cancel')}
        </DefaultButton>
        <PrimaryButton
          onClick={handleConfirm}
          color="primary"
          className={classes.confirmButton}
          disabled={loading}
        >
          {loading
            ? t('cohorts.notificationConfirmDialog.buttons.sending')
            : t('cohorts.notificationConfirmDialog.buttons.confirm')}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default NotificationConfirmDialog;
